import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import ImageUploadPreview from "../image-upload-preview";
import StepLayout from "../step-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";

const companyInfoSchema = z.object({
	jobTitle: z.string().optional(),
	companyName: z.string().optional(),
	websiteUrl: z
		.string()
		.optional()
		.refine((val) => !val || val.length === 0 || /^https?:\/\/.+/.test(val), {
			message: "Please enter a valid URL (e.g. https://example.com)",
		}),
});

type CompanyInfoFormData = z.infer<typeof companyInfoSchema>;

export default function StepAboutCompany() {
	const { data, setCompanyInfo, nextStep } = useFeedbackFlow();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<CompanyInfoFormData>({
		resolver: zodResolver(companyInfoSchema),
		mode: "onChange",
		defaultValues: {
			jobTitle: data.companyInfo.jobTitle,
			companyName: data.companyInfo.companyName,
			websiteUrl: data.companyInfo.websiteUrl,
		},
	});

	const onSubmit = (formData: CompanyInfoFormData) => {
		setCompanyInfo({
			jobTitle: formData.jobTitle || "",
			companyName: formData.companyName || "",
			websiteUrl: formData.websiteUrl || "",
		});
		nextStep();
	};

	const handleLogoChange = (file: File | null, preview: string | null) => {
		setCompanyInfo({
			logoFile: file,
			logoPreview: preview,
		});
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="h-full">
			<StepLayout
				header={
					<div className="mb-8">
						<h1 className="text-2xl font-semibold text-primary mb-2">About your company</h1>
						<p className="text-secondary">Help others understand where you work (all fields optional)</p>
					</div>
				}
				content={
					<div className="space-y-8">
						<Input label="Job title" type="text" placeholder="e.g. Head of Marketing" error={errors.jobTitle?.message} {...register("jobTitle")} />

						<Input label="Company name" type="text" placeholder="e.g. Acme Inc." error={errors.companyName?.message} {...register("companyName")} />

						<Input label="Company website" type="url" placeholder="e.g. https://example.com" error={errors.websiteUrl?.message} {...register("websiteUrl")} />

						<ImageUploadPreview label="Company logo (optional)" preview={data.companyInfo.logoPreview} onChange={handleLogoChange} />
					</div>
				}
				footer={
					<Button type="submit" variant="brand" className="w-full">
						Submit Testimonial
					</Button>
				}
			/>
		</form>
	);
}
